(() => {
  if (customElements.get('rr-gallery')) return;

  const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/\p{M}/gu, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  const RM = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class RRGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-go]')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.dotsWrap = this.querySelector('[data-dots]');
      this.currentEl = this.querySelector('[data-current]');
      this.totalEl = this.querySelector('[data-total]');

      this.applyColorFilter();
      this.buildDots();

      this.prev?.addEventListener('click', () => this.go(this.pos() - 1));
      this.next?.addEventListener('click', () => this.go(this.pos() + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goIndex(+t.dataset.go)));
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(this._raf); this._raf = requestAnimationFrame(() => this.sync()); }, { passive: true });
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.go(this.pos() - 1);
        if (e.key === 'ArrowRight') this.go(this.pos() + 1);
      });
      this.querySelectorAll('[data-zoom]').forEach((b) => b.addEventListener('click', () => this.openLightbox(+b.dataset.zoom)));
      this.querySelector('[data-close]')?.addEventListener('click', () => this.querySelector('[data-lightbox]').close());

      const start = +this.dataset.start || 0;
      requestAnimationFrame(() => { this.goIndex(start, true); this.sync(); });

      this.section = this.closest('.shopify-section, dialog');
      this.onVariant = this.onVariant.bind(this);
      this.section?.addEventListener('variant:updated', this.onVariant);
    }

    disconnectedCallback() {
      this.section?.removeEventListener('variant:updated', this.onVariant);
    }

    visible() { return this.slides.filter((s) => !s.hidden); }

    pos() {
      const vis = this.visible();
      const left = this.track.scrollLeft;
      let best = 0, dist = Infinity;
      vis.forEach((s, i) => { const d = Math.abs(s.offsetLeft - left); if (d < dist) { dist = d; best = i; } });
      return best;
    }

    // nombre de photos visibles à la fois (2 sur mobile : deux images côte à côte)
    pv() {
      const vis = this.visible();
      const s = vis[0];
      if (!s || getComputedStyle(this.track).display !== 'flex') return 1;
      const w = s.offsetWidth;
      return w > 0 ? Math.max(1, Math.min(vis.length, Math.round(this.track.clientWidth / w))) : 1;
    }

    go(i, instant) {
      const vis = this.visible();
      if (!vis.length) return;
      i = Math.max(0, Math.min(vis.length - this.pv(), i));
      const isGrid = getComputedStyle(this.track).display === 'grid';
      if (isGrid) return;
      this.track.scrollTo({ left: vis[i].offsetLeft, behavior: instant || RM() ? 'instant' : 'smooth' });
    }

    goIndex(dataIndex, instant) {
      const vis = this.visible();
      const i = vis.findIndex((s) => +s.dataset.index === dataIndex);
      this.go(i < 0 ? 0 : i, instant);
    }

    sync() {
      const vis = this.visible();
      const i = this.pos();
      const active = vis[i];
      if (this.currentEl) this.currentEl.textContent = i + 1;
      if (this.totalEl) this.totalEl.textContent = vis.length;
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= vis.length - this.pv();
      this.thumbs.forEach((t) => {
        const on = active && +t.dataset.go === +active.dataset.index;
        t.classList.toggle('is-active', on);
        if (on) {
          const wrap = t.parentElement;
          const l = t.offsetLeft - wrap.offsetLeft;
          if (l < wrap.scrollLeft || l + t.offsetWidth > wrap.scrollLeft + wrap.clientWidth) wrap.scrollTo({ left: l - wrap.clientWidth / 2 + t.offsetWidth / 2 });
        }
      });
      this.dotsWrap?.querySelectorAll('i').forEach((d, k) => d.classList.toggle('is-on', k === i));
      const count = this.querySelector('.rr-gal__count');
      if (count) count.hidden = vis.length < 2;
    }

    buildDots() {
      if (!this.dotsWrap) return;
      const n = this.visible().length;
      this.dotsWrap.innerHTML = n > 1 && n <= 12 ? '<i></i>'.repeat(n) : '';
    }

    // Ne garder que les photos du coloris choisi (une photo sans coloris identifié n'est jamais montrée)
    applyColorFilter() {
      this.slides.forEach((s) => { s.hidden = false; });
      this.thumbs.forEach((t) => { t.hidden = false; });
      if (this.dataset.filter !== 'true') return;
      const selected = norm(this.dataset.color);
      const colors = (this.dataset.colors || '').split('|').map(norm).filter(Boolean).sort((a, b) => b.length - a.length);
      if (!selected || colors.length < 2) return;

      const clean = (s) => s.replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
      const colorOf = (alt) => {
        const raw = (alt || '').toString();
        const tag = raw.match(/#(?:coloris|couleur|color)[_ ]([^#]+)/i);
        if (tag) return norm(tag[1]);
        const a = ' ' + clean(norm(alt)) + ' ';
        // le nom du coloris, au singulier ou au pluriel (bleu, bleus, vert, verts, beige clair...)
        return colors.find((c) => new RegExp(' ' + clean(c).split(' ').map((w) => w + '(?:s|x)?').join(' ') + ' ').test(a)) || null;
      };

      const tagged = this.slides.map((s) => colorOf(s.dataset.alt));
      if (!tagged.some((c) => c === selected)) return; // aucune photo pour ce coloris : on montre tout

      this.slides.forEach((s, k) => {
        const hide = tagged[k] !== selected;
        s.hidden = !!hide;
        const t = this.thumbs.find((th) => +th.dataset.go === +s.dataset.index);
        if (t) t.hidden = !!hide;
      });
    }

    // Les petites vignettes de la page (barre collante, médaillon « matière ») suivent le coloris choisi
    syncSatellites() {
      const first = this.slides.find((s) => !s.hidden);
      const im = first && first.querySelector('img');
      const u = im && (im.currentSrc || im.src);
      if (!u) return;
      document.querySelectorAll('.rr-sticky__img, .rr-pm__roimg').forEach((el) => {
        const w = el.classList.contains('rr-pm__roimg') ? 400 : 160;
        const base = u.replace(/([?&])width=\d+/, '$1width=' + w);
        const next = /[?&]width=/.test(base) ? base : base + (base.includes('?') ? '&' : '?') + 'width=' + w;
        if (el.getAttribute('src') !== next) { el.removeAttribute('srcset'); el.removeAttribute('sizes'); el.src = next; }
      });
    }

    onVariant(e) {
      const html = e.detail?.data?.html;
      const fresh = html?.querySelector?.('rr-gallery');
      if (fresh) {
        this.dataset.color = fresh.dataset.color || '';
        const badges = fresh.querySelector('.rr-gal__badges');
        const mine = this.querySelector('.rr-gal__badges');
        if (badges && mine) mine.innerHTML = badges.innerHTML;
      }
      this.applyColorFilter();
      this.buildDots();
      this.syncSatellites();
      const mediaId = e.detail?.resource?.featured_media?.id;
      const target = mediaId ? this.slides.find((s) => s.dataset.mediaId == mediaId && !s.hidden) : null;
      if (target) this.goIndex(+target.dataset.index);
      else this.go(0, true);
      requestAnimationFrame(() => this.sync());
    }

    openLightbox(dataIndex) {
      const dlg = this.querySelector('[data-lightbox]');
      const lb = this.querySelector('[data-lb-track]');
      if (!dlg || !lb || !dlg.showModal) return;
      const vis = this.visible().filter((s) => s.querySelector('img'));
      lb.innerHTML = vis.map((s) => {
        const img = s.querySelector('img');
        const src = img.currentSrc || img.src;
        const big = src.replace(/([?&])width=\d+/, '$1width=2400');
        return `<figure><img src="${big}" alt="${(img.alt || '').replace(/"/g, '&quot;')}"></figure>`;
      }).join('');
      dlg.showModal();
      const i = Math.max(0, vis.findIndex((s) => +s.dataset.index === dataIndex));
      lb.scrollLeft = lb.clientWidth * i;
      if (!dlg._rrBd) {
        dlg._rrBd = 1;
        dlg.addEventListener('click', (ev) => { if (ev.target === dlg) dlg.close(); });
      }
    }
  }

  customElements.define('rr-gallery', RRGallery);
})();

/* ===== Fiche produit, zone A : fondu desktop, rail vertical, plein écran et clavier (greffon, ne remplace rien) ===== */
(() => {
  const S = (window.__rrPdpA = window.__rrPdpA || {});
  if (S.gal) return;
  S.gal = 1;

  const patch = () => {
    const C = customElements.get('rr-gallery');
    if (!C || C.prototype.__rrA) return;
    const P = C.prototype;
    P.__rrA = 1;
    const oPos = P.pos, oGo = P.go, oSync = P.sync, oOpen = P.openLightbox;
    const mq = window.matchMedia('(min-width:990px)');
    const isFade = (g) => g.classList.contains('rr-fade');
    const RM = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Desktop : toutes les diapos sont superposées, la diapo courante porte .is-on (fondu), l'index est cyclique */
    P.pos = function () {
      if (!isFade(this)) return oPos.call(this);
      return Math.min(this._i || 0, Math.max(this.visible().length - 1, 0));
    };
    P.go = function (i, instant) {
      if (!isFade(this)) return oGo.call(this, i, instant);
      const vis = this.visible();
      const n = vis.length;
      if (!n) return;
      i = ((i % n) + n) % n;
      this._i = i;
      this.slides.forEach((s) => s.classList.toggle('is-on', s === vis[i]));
      this.sync();
    };
    P.sync = function () {
      oSync.call(this);
      if (isFade(this)) {
        if (this.prev) this.prev.disabled = false;
        if (this.next) this.next.disabled = false;
      }
      this.rail();
      this.zoomStops();
    };
    /* Un seul arrêt clavier par photo : la photo courante (le bouton plein écran prend le relais quand il est affiché) */
    P.zoomStops = function () {
      const fs = this.querySelector('.rr-gal__fs');
      const fsOn = !!fs && getComputedStyle(fs).display !== 'none';
      const vis = this.visible();
      const cur = vis[Math.min(this.pos(), Math.max(vis.length - 1, 0))];
      this.slides.forEach((s) => {
        const z = s.querySelector('[data-zoom]');
        if (!z) return;
        if (!fsOn && s === cur) z.removeAttribute('tabindex');
        else z.tabIndex = -1;
      });
    };
    /* Rail vertical : la vignette active reste visible */
    P.rail = function () {
      const t = this.querySelector('.rr-gal__thumb.is-active');
      const w = t && t.parentElement;
      if (!w || getComputedStyle(w).flexDirection !== 'column') return;
      const top = t.getBoundingClientRect().top - w.getBoundingClientRect().top + w.scrollTop;
      if (top < w.scrollTop || top + t.offsetHeight > w.scrollTop + w.clientHeight) {
        w.scrollTo({ top: top - w.clientHeight / 2 + t.offsetHeight / 2, behavior: RM() ? 'instant' : 'smooth' });
      }
    };
    /* Plein écran : flèches précédente et suivante, compteur « i / N » annoncé, clavier, balayage ; la galerie se resynchronise sur la dernière photo vue */
    P.openLightbox = function (dataIndex) {
      const opener = document.activeElement;
      oOpen.call(this, dataIndex);
      const dlg = this.querySelector('[data-lightbox]');
      const lb = this.querySelector('[data-lb-track]');
      if (!dlg || !lb || !dlg.open) return;
      const pick = (sel) => dlg.querySelector(sel);
      const total = () => lb.children.length;
      const paint = () => {
        const n = total();
        const k = Math.max(0, Math.min(n - 1, dlg._rrK || 0));
        const multi = n > 1;
        ['[data-lb-count]', '[data-lb-prev]', '[data-lb-next]'].forEach((q) => { const el = pick(q); if (el) el.hidden = !multi; });
        const cur = pick('[data-lb-cur]');
        const tot = pick('[data-lb-tot]');
        if (cur) cur.textContent = k + 1;
        if (tot) tot.textContent = n;
        const p = pick('[data-lb-prev]');
        const x = pick('[data-lb-next]');
        if (p) p.setAttribute('aria-disabled', k <= 0 ? 'true' : 'false');
        if (x) x.setAttribute('aria-disabled', k >= n - 1 ? 'true' : 'false');
      };
      const goTo = (k, instant) => {
        k = Math.max(0, Math.min(total() - 1, k));
        dlg._rrK = k;
        paint();
        lb.scrollTo({ left: k * lb.clientWidth, behavior: instant || RM() ? 'instant' : 'smooth' });
      };
      const vis = this.visible().filter((s) => s.querySelector('img'));
      dlg._rrOpener = opener;
      dlg._rrGo = goTo;
      goTo(Math.max(0, vis.findIndex((s) => +s.dataset.index === dataIndex)), true);
      if (dlg._rrA) return;
      dlg._rrA = 1;
      lb.addEventListener('scroll', () => {
        if (!lb.clientWidth) return;
        const k = Math.round(lb.scrollLeft / lb.clientWidth);
        if (k !== dlg._rrK) { dlg._rrK = k; paint(); }
      }, { passive: true });
      const step = (d) => { if (total() > 1) dlg._rrGo((dlg._rrK || 0) + d); };
      const bind = (sel, d) => {
        const b = pick(sel);
        if (b) b.addEventListener('click', () => { if (b.getAttribute('aria-disabled') !== 'true') step(d); });
      };
      bind('[data-lb-prev]', -1);
      bind('[data-lb-next]', 1);
      dlg.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        e.stopPropagation();
        step(e.key === 'ArrowRight' ? 1 : -1);
      });
      dlg.addEventListener('close', () => {
        const v = this.visible().filter((s) => s.querySelector('img'));
        const s = v[dlg._rrK || 0];
        if (s) this.goIndex(+s.dataset.index, true);
        const o = dlg._rrOpener;
        const t = o && o.matches && o.matches('[data-zoom]') && s ? (s.querySelector('[data-zoom]') || o) : o;
        if (t && t.isConnected) requestAnimationFrame(() => { try { t.focus({ preventScroll: true }); } catch (err) { /* sans effet */ } });
      });
    };

    const setMode = (g) => {
      if (!g.track) return;
      const want = mq.matches;
      if (want === isFade(g)) return;
      try {
        if (want) {
          const i = oPos.call(g);
          g.classList.add('rr-fade');
          g.track.scrollTo({ left: 0, behavior: 'instant' });
          g.go(i);
        } else {
          const i = g._i || 0;
          g.classList.remove('rr-fade');
          g.slides.forEach((s) => s.classList.remove('is-on'));
          const vis = g.visible();
          if (vis[i]) g.track.scrollTo({ left: vis[i].offsetLeft, behavior: 'instant' });
          g.sync();
        }
      } catch (err) {
        g.classList.remove('rr-fade');
      }
    };
    const wire = (g) => {
      if (g._rrW) return;
      g._rrW = 1;
      const fs = g.querySelector('.rr-gal__fs');
      if (fs) {
        fs.addEventListener('click', () => {
          const s = g.visible()[g.pos()];
          if (s) g.openLightbox(+s.dataset.index);
        });
      }
      if (mq.addEventListener) mq.addEventListener('change', () => setMode(g));
      setMode(g);
    };
    const all = () => document.querySelectorAll('rr-gallery').forEach(wire);
    all();
    document.addEventListener('shopify:section:load', all);
  };

  if (customElements.get('rr-gallery')) patch();
  else customElements.whenDefined('rr-gallery').then(patch);
})();
